import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors
} from '@angular/forms';
import { StatusUser } from '@core/enum/user';
import { ResponseListApi } from '@core/models/interfaces/common';
import { UserDetail } from '@core/models/interfaces/user';
import { UserService } from '@core/services/user.service';
import { CustomAvatarComponent } from '@shared/components/custom-avatar/custom-avatar.component';
import { SearchInputComponent } from '@shared/components/search-input/search-input.component';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { CheckboxModule } from 'primeng/checkbox';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { OverlayModule } from 'primeng/overlay';
import { NoteService } from '@core/services/note.service';
import { finalize, Observable } from 'rxjs';

@Component({
  selector: 'app-user-form-dropdown',
  templateUrl: './user-form-dropdown.component.html',
  styleUrl: './user-form-dropdown.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    HttpClientModule,
    InputTextModule,
    OverlayModule,
    InfiniteScrollDirective,
    IconFieldModule,
    InputIconModule,
    FloatLabelModule,
    SearchInputComponent,
    CustomAvatarComponent,
    CheckboxModule,
    FormsModule
  ],

  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UserFormDropdownComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => UserFormDropdownComponent),
      multi: true
    }
  ]
})
export class UserFormDropdownComponent implements ControlValueAccessor {
  @ViewChild('dropdownInput', { static: true }) dropdownInput!: ElementRef;

  @Input() placeholder: string = '';
  @Input() totalUsers = 0;
  @Input() options: any = [];
  @Input() optionLabel = 'fullName'; // Default label property
  @Input() optionValue = ''; // If set, will return this property value instead of full object
  @Input() isError = false;
  @Input() label: string = '';
  @Input() required = false;
  @Input() isFloatLabel: boolean = true;
  @Input() showClearIcon: boolean = false;
  @Input() role: string = '';
  @Input() styleClass = '';
  @Input() isDropdownIcon: boolean = false;
  @Input() multiple: boolean = false;
  @Input() userType: 'note' | 'permission' | undefined = undefined;

  @Output() onLazyLoad = new EventEmitter();

  page = 0;

  value: any;
  isVisible = false;
  isDisabled = false;

  onChange: any = () => {};
  onTouched: any = () => {};
  onValidatorChange: any = () => {};
  readonly StatusUser = StatusUser;

  keyword = '';

  // Internal selected options for multiple mode
  selectedOptions: any[] = [];

  loading: boolean = false;
  loadingMore: boolean = false;

  get valueStr() {
    if (this.multiple) {
      // Multiple selection - show comma-separated labels
      if (!this.selectedOptions || this.selectedOptions.length === 0) return '';
      return this.selectedOptions
        .map(item => this.getOptionLabel(item))
        .filter(label => label)
        .join(', ');
    } else {
      // Single selection
      if (!this.value) return '';

      // If value is primitive (optionValue was used), find the option
      const option = this.findOptionByValue(this.value);
      return option ? this.getOptionLabel(option) : '';
    }
  }

  constructor(
    private userService: UserService,
    private noteService: NoteService
  ) {}

  writeValue(value: any): void {
    this.value = value;

    if (this.multiple) {
      // Convert values to full options for display
      this.syncSelectedOptions();
    }
  }

  syncSelectedOptions(): void {
    if (!this.multiple || !this.value) {
      this.selectedOptions = [];
      return;
    }

    if (this.optionValue) {
      // Value is array of primitive values, map to full options
      this.selectedOptions = this.value
        .map((val: any) => this.findOptionByValue(val))
        .filter((opt: any) => opt !== null);
    } else {
      // Value is array of full objects.
      // Quan trọng: Cần match với options (nếu có) để PrimeNG checkbox so sánh đúng reference.
      if (!Array.isArray(this.value)) {
        this.selectedOptions = [];
        return;
      }

      // Nếu đã có options từ API, tìm option tương ứng theo id để dùng cùng reference.
      // Điều này giúp PrimeNG checkbox nhận ra item đã được select.
      if (this.options?.length) {
        this.selectedOptions = this.value
          .map((val: any) => {
            // Tìm option có cùng id trong options list
            const matchedOption = this.options.find(
              (opt: any) => opt?.id && val?.id && opt.id === val.id
            );
            // Nếu tìm thấy, dùng reference từ options; nếu không, giữ nguyên value
            return matchedOption || val;
          });
      } else {
        // Chưa có options, giữ nguyên value (minimal objects cho display)
        this.selectedOptions = [...this.value];
      }
    }
  }

  findOptionByValue(value: any): any {
    if (!this.options || this.options.length === 0) return null;

    if (this.optionValue) {
      return this.options.find((opt: any) => opt[this.optionValue] === value) || null;
    } else {
      // Compare by reference or id if available
      return this.options.find((opt: any) => opt === value || (opt.id && value.id && opt.id === value.id)) || null;
    }
  }

  /**
   * Lấy label để hiển thị cho option (VD: fullName của user).
   * Luôn trim để tránh khoảng trắng thừa (giống fix bên report production).
   */
  getOptionLabel(option: any): string {
    if (!option) return '';
    const raw = this.optionLabel ? option[this.optionLabel] : String(option);
    return typeof raw === 'string' ? raw.trim() : (raw ?? '');
  }

  getOptionValue(option: any): any {
    if (!option) return null;
    return this.optionValue ? option[this.optionValue] : option;
  }

  clearSelection(event: MouseEvent) {
    event.stopPropagation();
    this.value = this.multiple ? [] : undefined;
    this.selectedOptions = [];
    this.onChange(this.value);
    this.onTouched();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.required) {
      if (this.multiple) {
        if (!this.value || this.value.length === 0) {
          return { required: true };
        }
      } else {
        if (!this.value) {
          return { required: true };
        }
      }
    }

    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (this.dropdownInput) {
      this.dropdownInput.nativeElement.disabled = isDisabled;
    }
  }

  onSelectOption(option: any) {
    // Check if user is inactive or hidden
    if (option?.status?.code === StatusUser.HIDDEN || option?.status?.code === StatusUser.INACTIVE) {
      return;
    }

    const value = this.getOptionValue(option);
    this.value = value;
    this.onChange(value);
    this.isVisible = false;
  }

  onSelectOptions() {
    // Convert selected options to appropriate format
    if (this.optionValue) {
      this.value = this.selectedOptions.map(item => item[this.optionValue]);
    } else {
      this.value = [...this.selectedOptions];
    }

    this.onChange(this.value);
  }

  isOptionSelected(option: any): boolean {
    if (this.multiple) {
      if (this.optionValue) {
        const optionVal = option[this.optionValue];
        return this.selectedOptions.some(item => item[this.optionValue] === optionVal);
      } else {
        return this.selectedOptions.some(item => item === option || (item.id && option.id && item.id === option.id));
      }
    } else {
      if (!this.value) return false;

      if (this.optionValue) {
        return option[this.optionValue] === this.value;
      } else {
        return option === this.value || (option.id && this.value.id && option.id === this.value.id);
      }
    }
  }

  fetchUsers(type: 'SCROLL' | 'SEARCH') {
    let source: Observable<ResponseListApi<UserDetail>>;
    if (type === 'SEARCH') this.loading = true;

    if (this.role) {
      source = this.userService.getRoleAgentOrCSR(this.role, {
        page: this.page,
        size: 10,
        keyword: this.keyword,
        showHide: false,
        showInactive: false
      });
    } else if (this.userType === 'note') {
      source = this.noteService.getUsersNoteReport({
        page: this.page,
        size: 10,
        keyword: this.keyword
      });
    } else if (this.userType === 'permission') {
      source = this.userService.getUsersPermission({
        page: this.page,
        size: 10,
        keyword: this.keyword,
        showHide: false
      });
    } else {
      source = this.userService.getUsers({
        page: this.page,
        size: 10,
        keyword: this.keyword,
        showHide: false,
        showInactive: false
      });
    }

    source
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: res => {
          this.totalUsers = res.data.total;

          if (type === 'SCROLL') {
            const existingIds = new Set(this.options.map((o: any) => o.id));
            const newItems = res.data.content.filter((item: any) => !existingIds.has(item.id));
            this.options = [...this.options, ...newItems];
          }
          if (type === 'SEARCH') {
            this.options = res.data.content;
          }

          // Sync selected options after options load
          if (this.multiple) {
            this.syncSelectedOptions();
          }
        }
      });
  }

  onScroll() {
    if (this.totalUsers > this.options.length) {
      this.page++;
      this.fetchUsers('SCROLL');
    }
  }

  onSearch(keyword: string) {
    this.keyword = keyword;
    this.page = 0;
    this.fetchUsers('SEARCH');
  }

  onToggleVisible() {
    if (!this.isDisabled) {
      this.isVisible = !this.isVisible;
    }

    if (this.isVisible && !this.keyword && this.options?.length === 0 && this.page === 0) {
      this.page = 0;
      this.fetchUsers('SEARCH');
    }
  }
}
